import { useState, useEffect, useCallback, useRef } from "react";
import { SavedSidebar } from "@/pages/Index/SavedSidebar";
import TutorialOverview from "@/components/TutorialOverview";
import { useTutorials } from "@/hooks/useTutorials";
import { MainLayout } from "@/components/layouts/MainLayout";
import { FilterComponent } from "@/components/FilterComponent";
import { useAllTutorialStats } from "@/hooks/useTutorialStats";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "react-i18next";
import ProfilePage from "../ProfilePage";

import { TagPreferenceType, ToolPreferenceType } from "@/lib/supabase";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";
import { CourseProgressBar } from "@/components/ui/CourseProgressBar";
import { useTabNavigation } from "@/stores/use-nav-store";
import { useInitialRedirect } from "@/hooks/useInitialRedirect";
import { isSpecialTagSelected } from "@/utils/isSpecialTagSelected";

type UpdateTagPreferenceFn = {
  mutate: (variables: {
    tagName: string;
    preferenceType: TagPreferenceType | null;
  }) => void;
};

const getStoredPreferences = (
  key: string,
  defaultValue: { included: string[]; excluded: string[] }
) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (e) {
    console.error(`Failed to parse ${key} from sessionStorage:`, e);
    return defaultValue;
  }
};

const Index = () => {
  const { t } = useTranslation();
  const { activeTab, setActiveTab, isLargeScreen } = useTabNavigation();
  useInitialRedirect({ redirectTo: "/community" });

  const PAGE_SIZE = 12;
  const tutorialRefs = useRef<(HTMLElement | null)[]>([]);

  const [tagPreferences, setTagPreferences] = useState<{
    included: string[];
    excluded: string[];
  }>(() =>
    getStoredPreferences("tagPreferences", { included: [], excluded: [] })
  );

  const updateTagPreference = {
    mutate: ({
      tagName,
      preferenceType,
    }: {
      tagName: string;
      preferenceType: string | null;
    }) => {
      setTagPreferences((prev) => {
        const included = [];
        const excluded = [];

        setIsExpanded(false);
        if (preferenceType === "included") {
          MixpanelActions.tagSelected(tagName);
          included.push(tagName);
        } else if (preferenceType === "excluded") excluded.push(tagName);
        return { included, excluded };
      });
    },
    data: undefined,
    error: null,
    variables: undefined,
    isError: false,
    isIdle: true,
    isLoading: false,
    isPaused: false,
    isPending: false,
    isSuccess: false,
    reset: () => {},
    status: "idle",
    failureCount: 0,
    failureReason: null,
    mutateAsync: async () => {},
    context: undefined,
    submittedAt: undefined,
  };

  const {
    tutorials,
    isLoading: isTutorialsLoading,
    isRefetching,
    uninterestedIds,
    completedIds,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTutorials({
    selectedTags: tagPreferences.included,
    excludedTags: tagPreferences.excluded,
  });
  const isLoadingTutorials = isTutorialsLoading || isRefetching;
  const [isExpanded, setIsExpanded] = useState(false);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry?.isIntersecting && !isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    sessionStorage.setItem("tagPreferences", JSON.stringify(tagPreferences));
  }, [tagPreferences]);

  useEffect(() => {
    const lastTutorialId = sessionStorage.getItem("lastTutorialId");
    if (lastTutorialId && tutorials.length > 0) {
      requestAnimationFrame(() => {
        const targetIndex = tutorials.findIndex(
          (t) => t.documentId === lastTutorialId
        );
        if (targetIndex !== -1) {
          const el = tutorialRefs.current[targetIndex];
          el?.scrollIntoView({ behavior: "auto", block: "center" });
        }
        sessionStorage.removeItem("lastTutorialId");
      });
    }
  }, [tutorials]);

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [tagPreferences]);

  useEffect(() => {
    const createSentinel = () => {
      const lastTutorial = document.querySelector(
        ".grid-cols-1.gap-6 > *:nth-last-child(3)"
      );
      if (!lastTutorial) return null;
      const sentinel = document.createElement("div");
      sentinel.id = "scroll-sentinel";
      sentinel.style.height = "1px";
      sentinel.style.width = "100%";
      sentinel.style.position = "absolute";
      sentinel.style.opacity = "0";
      lastTutorial.appendChild(sentinel);
      return sentinel;
    };

    const sentinelObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !isFetchingNextPage && hasNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "500px 0px" }
    );

    // Main observer as backup
    const mainObserver = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: "100px 0px",
    });

    // Set up the observers
    const sentinel = createSentinel();
    const trigger = document.getElementById("infinite-scroll-trigger");
    if (sentinel) sentinelObserver.observe(sentinel);
    if (trigger) mainObserver.observe(trigger);

    return () => {
      if (sentinel) sentinelObserver.unobserve(sentinel);
      if (trigger) mainObserver.unobserve(trigger);
      document.getElementById("scroll-sentinel")?.remove();
    };
  }, [
    handleObserver,
    tutorials.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const {
    ratings,
    completedCounts,
    isLoading: isStatsLoading,
  } = useAllTutorialStats();

  const SidebarContent = useCallback(
    () => (
      <SavedSidebar
        uninterestedIds={uninterestedIds}
        completedIds={completedIds}
        isLoading={isTutorialsLoading}
        tagPreferences={tagPreferences}
        onTagPreferenceChange={(value) => {
          setActiveTab("menu");
          setTagPreferences(value);
        }}
        isLargeScreen={isLargeScreen}
      />
    ),
    [
      tagPreferences,
      setTagPreferences,
      uninterestedIds,
      completedIds,
      isTutorialsLoading,
      isLargeScreen,
      setActiveTab,
    ]
  );
  return (
    <MainLayout>
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] gap-0 lg:gap-8">
        <div className="-mt-4 hidden lg:block">
          {!isSpecialTagSelected(tagPreferences.included) && (
            <FilterComponent
              tagPreferences={tagPreferences}
              updateTagPreference={updateTagPreference as UpdateTagPreferenceFn}
              isLargeScreen={isLargeScreen}
            />
          )}
        </div>

        <main className="space-y-2 md:space-y-0">
          <div className="relative grid grid-cols-1 gap-3">
            {/* Loading state */}
            {(isLoadingTutorials || isStatsLoading) && (
              <div className="animate-pulse space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-48 bg-gray-200 rounded-lg" />
                ))}
              </div>
            )}

            {/* Tutorials content */}
            {!isLoadingTutorials && !isStatsLoading && (
              <>
                {/* Progress bar */}
                {activeTab === "menu" && (
                  <>
                    <CourseProgressBar
                      isExpanded={isExpanded}
                      setIsExpanded={setIsExpanded}
                      isLargeScreen={isLargeScreen}
                      tagPreferences={tagPreferences}
                    />
                  </>
                )}

                {activeTab === "menu" &&
                  !isExpanded &&
                  tutorials.map((tutorial, index) => (
                    <TutorialOverview
                      ref={(tutorial) =>
                        (tutorialRefs.current[index] = tutorial)
                      }
                      key={tutorial.documentId}
                      tutorial={tutorial}
                      rating={ratings[tutorial.documentId]}
                      completedCount={completedCounts[tutorial.documentId]}
                    />
                  ))}
                {activeTab === "bookmarks" && !isExpanded && (
                  <div className="text-left">{SidebarContent()}</div>
                )}
                {activeTab === "profile" && !isExpanded && <ProfilePage />}
                {isExpanded && (
                  <FilterComponent
                    tagPreferences={tagPreferences}
                    updateTagPreference={
                      updateTagPreference as UpdateTagPreferenceFn
                    }
                    isLargeScreen={isLargeScreen}
                  />
                )}
              </>
            )}

            {!isLoadingTutorials && !tutorials.length && (
              <div className="text-center text-gray-500">
                {t("common.noTutorialsFound")}
              </div>
            )}
          </div>

          {/* Infinite scroll loading indicator */}
          {tutorials.length > 0 && activeTab !== "profile" && (
            <div
              id="infinite-scroll-trigger"
              className="flex justify-center py-6"
            >
              {isFetchingNextPage && <Spinner className="h-8 w-8" />}
              {!isLoadingTutorials &&
                !isFetchingNextPage &&
                !hasNextPage &&
                tutorials.length > PAGE_SIZE && (
                  <div className="text-sm text-muted-foreground py-2">
                    {t("common.noMoreResults")}
                  </div>
                )}
            </div>
          )}
        </main>

        {/* Hide SavedSidebar here on mobile */}
        <aside className="-mt-4 hidden lg:block">
          <div className="sticky top-[4.5rem]">{SidebarContent()}</div>
        </aside>
      </div>
    </MainLayout>
  );
};

export default Index;
